import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { useMocks } from '@config/env';
import { emailFromUser, isAdmin } from './adminClaims';
import { nextAuthStep } from './authStep';
import { NotAuthorizedPage } from './NotAuthorizedPage';

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Gates the WHOLE app. Two layers:
 *  1. Authentication — in Cognito mode, an unauthenticated visitor is redirected to the hosted UI. A
 *     stored session whose access token has expired is first refreshed silently with its refresh token
 *     (see `authStep.ts`), so reopening the browser inside the 7-day window costs no password or code.
 *  2. Authorization  — once authenticated, the token's `cognito:groups` MUST include "admin", else
 *     the clean Not-Authorized dead-end renders (with sign-out). No admin group → no access.
 *
 * In mock mode (no backend / Cognito), both layers are bypassed so the UI is fully demoable.
 */
export function AuthGuard({ children }: AuthGuardProps) {
  if (useMocks) return <>{children}</>;
  return <CognitoGuard>{children}</CognitoGuard>;
}

function CognitoGuard({ children }: AuthGuardProps) {
  const auth = useAuth();
  // Back to 'idle' after a successful refresh, so a later expiry in the same tab (the automatic renew
  // failed mid-session) gets its own attempt instead of hanging on "Signing you in…".
  const [refresh, setRefresh] = useState<'idle' | 'running' | 'failed'>('idle');
  const step = nextAuthStep({
    isLoading:       auth.isLoading,
    activeNavigator: auth.activeNavigator,
    isAuthenticated: auth.isAuthenticated,
    hasRefreshToken: !!auth.user?.refresh_token,
    refreshFailed:   refresh === 'failed',
  });

  useEffect(() => {
    if (step === 'refresh' && refresh === 'idle') {
      setRefresh('running');
      // Resolves null rather than throwing on failure; react-oidc-context records the reason in auth.error.
      void auth.signinSilent().then(user => setRefresh(user ? 'idle' : 'failed'));
    } else if (step === 'redirect') {
      void auth.signinRedirect();
    }
  }, [step, refresh, auth]);

  if (step === 'wait' || step === 'refresh')
    return <FullPageStatus label="Signing you in…" />;
  // A live session wins over a stale error: a failed background renew must not hide an access token that
  // is still valid. And a failed silent refresh is the expected end of the week, not an error to show.
  if (step !== 'ready' && auth.error && refresh !== 'failed')
    return <FullPageStatus label={`Sign-in error: ${auth.error.message}`} />;
  if (step === 'redirect')
    return <FullPageStatus label="Redirecting to sign-in…" />;

  // Authenticated — now enforce the admin group on every render.
  if (!isAdmin(auth.user)) return <NotAuthorizedPage email={emailFromUser(auth.user)} />;

  return <>{children}</>;
}

function FullPageStatus({ label }: { label: string }) {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <p className="text-text-muted">{label}</p>
    </div>
  );
}
